/**
 * Merchant_dashboard.jsx
 * ESDRAS MERCHANT MARKETPLACE — Merchant Dashboard
 * Route: /merchant/dashboard (MerchantProtectedLayout)
 * Tabs: MY PRODUCTS | UPLOAD | ORDERS | ANALYTICS | WALLET | PROFILE
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, query, where, orderBy, onSnapshot,
  doc, getDoc, updateDoc, deleteDoc, addDoc,
  serverTimestamp, getDocs, setDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { db, auth, storage } from '../../firebase';
import { toast } from 'react-hot-toast';

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const GOLD          = '#B8860B';
const DEEP_BLACK    = '#050a0f';
const CARD_BG       = '#0a0f14';
const CARD_BG2      = '#0d1520';
const BORDER        = '#1a2631';
const PLATFORM_FEE  = 0.10;
const DELIVERY_NGN  = 2500;
const SAFE_IMG      = 'https://placehold.co/400x400/050a0f/B8860B?text=Product';
const SUPER_ADMINS  = ['FFXDokpj5IXR3SFSvJyDu70LRp73', 'GbjKMdeVEmTgMefcU1cEHsEOstn1'];

const TABS = [
  { id: 'products',  icon: '📦', label: 'MY PRODUCTS' },
  { id: 'upload',    icon: '⬆️', label: 'UPLOAD' },
  { id: 'orders',    icon: '🛒', label: 'ORDERS' },
  { id: 'analytics', icon: '📊', label: 'ANALYTICS' },
  { id: 'wallet',    icon: '💰', label: 'WALLET' },
  { id: 'profile',   icon: '👤', label: 'PROFILE' },
];

const STATUS_COLORS = {
  pending_approval: { bg: '#1a1200', color: '#f59e0b', label: 'PENDING' },
  approved:         { bg: '#0a1f0a', color: '#4caf50', label: 'APPROVED' },
  rejected:         { bg: '#1a0a0a', color: '#ff4d4d', label: 'REJECTED' },
  suspended:        { bg: '#1a0a1a', color: '#a855f7', label: 'SUSPENDED' },
};

const ORDER_STATUSES = ['paid', 'processing', 'dispatched', 'delivered', 'received'];

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function Merchant_dashboard() {
  const navigate = useNavigate();

  const [user,     setUser]     = useState(null);
  const [merchant, setMerchant] = useState(null);
  const [wallet,   setWallet]   = useState(null);
  const [tab,      setTab]      = useState('products');
  const [loading,  setLoading]  = useState(true);

  // Data
  const [products, setProducts] = useState([]);
  const [orders,   setOrders]   = useState([]);

  // ── Auth + Load Merchant ──────────────────────────────────────────────────
useEffect(() => {
  const unsub = onAuthStateChanged(auth, async (u) => {
    if (!u) { 
      navigate('/'); 
      return; 
    }

    // SUPER ADMINS: Allow access to merchant dashboard (no redirect to admin)
    if (SUPER_ADMINS.includes(u.uid)) {
      setUser(u);
      // Create a dummy merchant object so the UI doesn't break
      setMerchant({ 
        id: u.uid,
        businessName: "SUPER ADMIN PREVIEW MODE", 
        status: "approved",
        isSuperAdminView: true 
      });
      setLoading(false);
      return;
    }

    // Normal merchant flow
    setUser(u);
    const mSnap = await getDoc(doc(db, 'merchants', u.uid));
    
    if (!mSnap.exists() || mSnap.data().status !== 'approved') {
      toast.error('Merchant access denied.');
      navigate('/');
      return;
    }

    setMerchant({ id: mSnap.id, ...mSnap.data() });
    setLoading(false);
  });

  return () => unsub();
}, [navigate]);

  // ── Real-time products ───────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'merchant_products'),
      where('merchantId', '==', user.uid),
      orderBy('createdAt', 'desc'),
    );
    return onSnapshot(q, snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => {});
  }, [user]);

  // ── Real-time orders ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'merchant_orders'),
      where('merchantId', '==', user.uid),
      orderBy('createdAt', 'desc'),
    );
    return onSnapshot(q, snap => setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      () => {});
  }, [user]);

  // ── Real-time wallet ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    return onSnapshot(doc(db, 'merchant_wallets', user.uid),
      snap => { if (snap.exists()) setWallet(snap.data()); },
      () => {});
  }, [user]);

  const handleSignOut = async () => {
    await signOut(auth);
    navigate('/');
  };

  if (loading) return <LoadingScreen />;

  // ─── STATS BAR ──────────────────────────────────────────────────────────
  const liveProducts  = products.filter(p => p.status === 'approved').length;
  const pendingProd   = products.filter(p => p.status === 'pending_approval').length;
  const totalOrders   = orders.length;
  const totalRevenue  = orders.filter(o => o.paymentStatus === 'paid').reduce((s, o) => s + (o.basePrice * o.qty), 0);

  return (
    <div style={S.page}>
      {/* ── Sidebar ── */}
      <aside style={S.sidebar}>
        <div style={S.sideTop}>
          <img src="/icons/esdras-logo.png" alt="Esdras" style={{ width: '36px', marginBottom: '4px' }}
            onError={e => { e.target.style.display = 'none'; }} />
          <p style={S.sideTitle}>MERCHANT</p>
          <p style={S.sideTitle2}>PORTAL</p>
        </div>

        {/* Merchant Avatar */}
        <div style={S.merchantCard}>
          <div style={S.avatarCircle}>
            {merchant?.logoUrl
              ? <img src={merchant.logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
              : <span style={{ color: GOLD, fontWeight: '900', fontSize: '1.3rem' }}>
                  {(merchant?.businessName || 'M')[0].toUpperCase()}
                </span>}
          </div>
          <p style={S.mBizName}>{merchant?.businessName || 'My Business'}</p>
          <p style={S.mEmail}>{user?.email}</p>
          <div style={S.approvedBadge}>✅ APPROVED</div>
        </div>

        {/* Nav tabs */}
        <nav style={S.nav}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ ...S.navBtn, ...(tab === t.id ? S.navBtnActive : {}) }}>
              <span style={{ fontSize: '1rem' }}>{t.icon}</span>
              <span>{t.label}</span>
              {t.id === 'orders' && orders.filter(o => o.orderStatus === 'processing').length > 0 && (
                <span style={S.badge}>{orders.filter(o => o.orderStatus === 'processing').length}</span>
              )}
            </button>
          ))}
        </nav>

        <button onClick={handleSignOut} style={S.signOutBtn}>⟵ SIGN OUT</button>
      </aside>

      {/* ── Main Content ── */}
      <main style={S.main}>
        {/* Stats Bar */}
        <div style={S.statsBar}>
          {[
            { label: 'LIVE PRODUCTS', value: liveProducts, color: '#4caf50' },
            { label: 'PENDING',        value: pendingProd, color: '#f59e0b' },
            { label: 'TOTAL ORDERS',   value: totalOrders, color: GOLD },
            { label: 'TOTAL EARNED',   value: `₦${totalRevenue.toLocaleString()}`, color: GOLD },
            { label: 'WALLET',         value: `₦${(wallet?.availableBalance || 0).toLocaleString()}`, color: '#4caf50' },
          ].map(s => (
            <div key={s.label} style={S.statCard}>
              <p style={{ color: s.color, fontWeight: '900', fontSize: '1.3rem', margin: '0 0 4px' }}>{s.value}</p>
              <p style={{ color: '#444', fontSize: '0.6rem', letterSpacing: '1.5px', margin: 0 }}>{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tab Content */}
        <div style={S.content}>
          {tab === 'products'  && <ProductsTab  products={products} user={user} navigate={navigate} />}
          {tab === 'upload'    && <UploadTab    user={user} merchant={merchant} onUploaded={() => setTab('products')} />}
          {tab === 'orders'    && <OrdersTab    orders={orders} />}
          {tab === 'analytics' && <AnalyticsTab orders={orders} products={products} />}
          {tab === 'wallet' && (
            <div style={{ 
              padding: '60px 20px', 
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px'
            }}>
              <div style={{ fontSize: '3.5rem', marginBottom: '20px' }}>💰</div>
    
              <h3 style={{ 
                color: GOLD, 
                fontWeight: '900', 
                fontSize: '1.35rem', 
                margin: '0 0 12px',
                letterSpacing: '1px'
              }}>
                MERCHANT WALLET
              </h3>
    
              <p style={{ 
                color: '#888', 
                fontSize: '0.95rem', 
                maxWidth: '380px', 
                lineHeight: '1.5',
                marginBottom: '32px'
                }}>
                  View your available balance, pending funds, full transaction history, 
                  and initiate secure withdrawals.
                </p>

                <button
                  onClick={() => navigate('/merchant/wallet')}
                  style={{
                    ...S.goldBtn,
                    fontSize: '1.05rem',
                    padding: '18px 48px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '12px',
                    boxShadow: `0 4px 20px #B8860B33`
                  }}
                 >
                  OPEN FULL WALLET →
                </button>

                <p style={{ 
                  color: '#555', 
                  marginTop: '24px', 
                  fontSize: '0.78rem',
                  fontStyle: 'italic'
                }}>
                  Includes CSV export • Cursor pagination • Withdrawal confirmation
                </p>
              </div>
            )}
          {tab === 'profile'   && <ProfileTab_FIXED   merchant={merchant} user={user} setMerchant={setMerchant} />}
        </div>
      </main>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// TAB: MY PRODUCTS
// ════════════════════════════════════════════════════════════
function ProductsTab({ products, user, navigate }) {
  const [filter, setFilter] = useState('all');

  const filtered = filter === 'all' ? products : products.filter(p => p.status === filter);

  const handleDelete = async (prod) => {
    if (!window.confirm(`Delete "${prod.name}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, 'merchant_products', prod.id));
      toast.success('Product deleted.');
    } catch { toast.error('Delete failed.'); }
  };

  const handleShare = (prod) => {
    const url = `${window.location.origin}/marketplace/product/${prod.id}`;
    navigator.clipboard.writeText(url);
    toast.success('Product link copied!');
  };

  return (
    <div>
      <TabHeader title="MY PRODUCTS" sub={`${products.length} total`} />

      {/* Filter Pills */}
      <div style={S.filterRow}>
        {[['all', 'All'], ['approved', 'Live'], ['pending_approval', 'Pending'], ['rejected', 'Rejected']].map(([val, label]) => (
          <button key={val} onClick={() => setFilter(val)}
            style={{ ...S.filterPill, ...(filter === val ? S.filterPillActive : {}) }}>
            {label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon="📦" title="No products here" sub="Upload your first product to get started." />
      ) : (
        <div style={S.productGrid}>
          {filtered.map(prod => (
            <div key={prod.id} style={S.productCard}>
              {/* Image */}
              <div style={S.prodImgBox}>
                <img src={prod.imageUrls?.[0] || SAFE_IMG} alt={prod.name}
                  style={S.prodImg} onError={e => { e.target.src = SAFE_IMG; }} />
                <div style={{ ...S.statusBadge, background: STATUS_COLORS[prod.status]?.bg, color: STATUS_COLORS[prod.status]?.color }}>
                  {STATUS_COLORS[prod.status]?.label}
                </div>
              </div>

              {/* Info */}
              <div style={{ padding: '12px' }}>
                <p style={{ color: 'white', fontWeight: '700', fontSize: '0.9rem', margin: '0 0 3px', lineHeight: '1.3' }}>{prod.name}</p>
                <p style={{ color: '#555', fontSize: '0.68rem', margin: '0 0 6px', letterSpacing: '0.5px' }}>
                  {prod.category === 'hair_care' ? '💆 HAIR CARE' : '✨ SKIN CARE'}
                </p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ color: GOLD, fontWeight: '900', fontSize: '1rem', margin: 0 }}>₦{Number(prod.displayPrice).toLocaleString()}</p>
                    <p style={{ color: '#444', fontSize: '0.65rem', margin: '2px 0 0' }}>Base: ₦{Number(prod.basePrice).toLocaleString()}</p>
                  </div>
                  <p style={{ color: prod.qty > 0 ? '#4caf50' : '#ff4d4d', fontSize: '0.72rem', fontWeight: '700', margin: 0 }}>
                    {prod.qty > 0 ? `${prod.qty} left` : 'OUT'}
                  </p>
                </div>

                {/* Rejection reason */}
                {prod.status === 'rejected' && prod.rejectionReason && (
                  <div style={{ background: '#1a0a0a', border: '1px solid #ff4d4d33', borderRadius: '8px', padding: '8px', marginTop: '8px' }}>
                    <p style={{ color: '#ff6666', fontSize: '0.7rem', margin: 0, lineHeight: '1.5' }}>
                      <strong>Reason:</strong> {prod.rejectionReason}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: '6px', marginTop: '10px' }}>
                  <button onClick={() => handleShare(prod)} style={S.actionBtn}>🔗 Share</button>
                  <button onClick={() => handleDelete(prod)} style={{ ...S.actionBtn, color: '#ff4d4d', borderColor: '#ff4d4d44' }}>🗑</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// TAB: UPLOAD PRODUCT
// ════════════════════════════════════════════════════════════
function UploadTab({ user, merchant, onUploaded }) {
  const [form, setForm] = useState({
    name: '', category: 'hair_care', description: '',
    nafdacNumber: '', qty: 1, basePrice: '',
  });
  const [images,   setImages]   = useState([null, null, null]);
  const [loading,  setLoading]  = useState(false);

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const platformFee  = form.basePrice ? Math.round(Number(form.basePrice) * PLATFORM_FEE) : 0;
  const displayPrice = form.basePrice ? Number(form.basePrice) + platformFee : 0;

  const handleImageSlot = (i, file) => {
    const updated = [...images];
    updated[i] = file;
    setImages(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validImages = images.filter(Boolean);
    if (validImages.length === 0) { toast.error('Upload at least one product image.'); return; }
    if (!form.nafdacNumber.trim()) { toast.error('NAFDAC registration number is required.'); return; }
    if (Number(form.basePrice) <= 0) { toast.error('Please enter a valid base price.'); return; }

    setLoading(true);
    try {
      const imgUrls = [];
      for (const file of validImages) {
        const r = ref(storage, `merchant_products/${user.uid}/${Date.now()}_${file.name}`);
        await uploadBytes(r, file);
        imgUrls.push(await getDownloadURL(r));
      }

      await addDoc(collection(db, 'merchant_products'), {
        merchantId:          user.uid,
        merchantName:        merchant?.businessName || '',
        merchantProfileImageUrl: merchant?.logoUrl || '',
        name:                form.name.trim(),
        category:            form.category,
        description:         form.description.trim(),
        nafdacNumber:        form.nafdacNumber.trim(),
        qty:                 Number(form.qty),
        basePrice:           Number(form.basePrice),
        platformFee,
        displayPrice,
        imageUrls:           imgUrls,
        status:              'pending_approval',
        rejectionReason:     null,
        approvedBy:          null,
        approvedAt:          null,
        liveOnHomepage:      false,
        shareableProductLink:'',
        averageRating:       0,
        reviewCount:         0,
        totalSold:           0,
        createdAt:           serverTimestamp(),
        updatedAt:           serverTimestamp(),
      });

      toast.success('Product submitted for approval! We\'ll review it shortly.');
      setForm({ name: '', category: 'hair_care', description: '', nafdacNumber: '', qty: 1, basePrice: '' });
      setImages([null, null, null]);
      onUploaded?.();
    } catch (err) {
      toast.error('Upload failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <TabHeader title="UPLOAD PRODUCT" sub="All products require admin approval before going live" />
      <form onSubmit={handleSubmit} style={{ maxWidth: '600px', display: 'flex', flexDirection: 'column', gap: '14px' }}>

        <FieldLabel>PRODUCT CATEGORY</FieldLabel>
        <div style={{ display: 'flex', gap: '10px' }}>
          {[['hair_care', '💆 HAIR CARE'], ['skin_care', '✨ SKIN CARE']].map(([v, l]) => (
            <button type="button" key={v} onClick={() => setField('category', v)}
              style={{ ...S.toggleBtn, ...(form.category === v ? S.toggleActive : S.toggleInactive) }}>
              {l}
            </button>
          ))}
        </div>

        <FieldLabel>PRODUCT NAME <Req /></FieldLabel>
        <input value={form.name} onChange={e => setField('name', e.target.value)}
          placeholder="e.g. Natural Shea Butter Curl Cream" style={S.input} required />

        <FieldLabel>DESCRIPTION <Req /></FieldLabel>
        <textarea value={form.description} onChange={e => setField('description', e.target.value)}
          placeholder="Describe your product — ingredients, benefits, how to use, size/volume..."
          style={{ ...S.input, minHeight: '100px', resize: 'vertical' }} required />

        <FieldLabel>NAFDAC REGISTRATION NUMBER <Req /></FieldLabel>
        <input value={form.nafdacNumber} onChange={e => setField('nafdacNumber', e.target.value)}
          placeholder="e.g. A7-1234" style={S.input} required />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <FieldLabel>QTY IN STOCK <Req /></FieldLabel>
            <input type="number" min="1" value={form.qty}
              onChange={e => setField('qty', e.target.value)} style={S.input} required />
          </div>
          <div>
            <FieldLabel>BASE PRICE (₦) <Req /></FieldLabel>
            <input type="number" min="1" value={form.basePrice}
              onChange={e => setField('basePrice', e.target.value)}
              placeholder="e.g. 10000" style={S.input} required />
          </div>
        </div>

        {/* Fee Breakdown */}
        {form.basePrice > 0 && (
          <div style={S.feeBox}>
            <div style={S.feeRow}>
              <span style={S.feeLabel}>Your base price</span>
              <span style={{ color: 'white', fontWeight: '700' }}>₦{Number(form.basePrice).toLocaleString()}</span>
            </div>
            <div style={S.feeRow}>
              <span style={S.feeLabel}>Platform fee (10%) — retained by Esdras</span>
              <span style={{ color: '#f59e0b' }}>+ ₦{platformFee.toLocaleString()}</span>
            </div>
            <div style={{ ...S.feeRow, borderTop: `1px solid ${BORDER}`, paddingTop: '10px', marginTop: '4px' }}>
              <span style={{ color: 'white', fontWeight: '900', fontSize: '0.85rem' }}>Buyers will see</span>
              <span style={{ color: GOLD, fontWeight: '900', fontSize: '1.1rem' }}>₦{displayPrice.toLocaleString()}</span>
            </div>
            <p style={{ color: '#555', fontSize: '0.72rem', margin: '6px 0 0', lineHeight: '1.5' }}>
              You receive ₦{Number(form.basePrice).toLocaleString()} per unit sold. Delivery fees (₦2,500–₦10,000) are separate.
            </p>
          </div>
        )}

        {/* Image Upload — 3 slots */}
        <FieldLabel>PRODUCT IMAGES (UP TO 3) <Req /></FieldLabel>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
          {[0, 1, 2].map(i => (
            <label key={i} style={{
              aspectRatio: '1/1', background: CARD_BG2,
              border: `1.5px dashed ${images[i] ? GOLD : BORDER}`,
              borderRadius: '12px', display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', position: 'relative', overflow: 'hidden',
            }}>
              {images[i] ? (
                <>
                  <img src={URL.createObjectURL(images[i])} alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }} />
                  <div style={{ position: 'absolute', bottom: '4px', left: '4px', background: 'rgba(0,0,0,0.7)', borderRadius: '4px', padding: '2px 5px' }}>
                    <span style={{ color: GOLD, fontSize: '0.6rem', fontWeight: '700' }}>Image {i + 1}</span>
                  </div>
                </>
              ) : (
                <>
                  <span style={{ fontSize: '1.5rem', color: '#2a3641' }}>📷</span>
                  <span style={{ color: '#333', fontSize: '0.65rem', marginTop: '5px' }}>
                    {i === 0 ? 'Main *' : `Image ${i + 1}`}
                  </span>
                </>
              )}
              <input type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => e.target.files[0] && handleImageSlot(i, e.target.files[0])} />
            </label>
          ))}
        </div>

        <button type="submit" disabled={loading} style={{ ...S.goldBtn, opacity: loading ? 0.6 : 1 }}>
          {loading ? 'UPLOADING...' : 'SUBMIT FOR APPROVAL'}
        </button>
      </form>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// TAB: ORDERS
// ════════════════════════════════════════════════════════════
function OrdersTab({ orders }) {
  const [filter, setFilter] = useState('all');
  const filtered = filter === 'all' ? orders : orders.filter(o => o.orderStatus === filter);

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await updateDoc(doc(db, 'merchant_orders', orderId), {
        orderStatus: newStatus, updatedAt: serverTimestamp(),
      });
      toast.success(`Order marked as ${newStatus}.`);
    } catch { toast.error('Update failed.'); }
  };

  const ORDER_STATUS_COLORS = {
    paid:       { color: '#f59e0b', bg: '#1a1200' },
    processing: { color: GOLD,      bg: '#1a1200' },
    dispatched: { color: '#60a5fa', bg: '#0a1020' },
    delivered:  { color: '#4caf50', bg: '#0a1f0a' },
    received:   { color: '#888',    bg: '#1a1a1a' },
  };

  return (
    <div>
      <TabHeader title="ORDERS" sub={`${orders.length} total orders`} />

      <div style={S.filterRow}>
        {[['all', 'All'], ...ORDER_STATUSES.map(s => [s, s.charAt(0).toUpperCase() + s.slice(1)])].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)}
            style={{ ...S.filterPill, ...(filter === v ? S.filterPillActive : {}) }}>{l}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon="🛒" title="No orders yet" sub="Your customer orders will appear here." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filtered.map(order => {
            const sc = ORDER_STATUS_COLORS[order.orderStatus] || ORDER_STATUS_COLORS.paid;
            const nextStatus = ORDER_STATUSES[ORDER_STATUSES.indexOf(order.orderStatus) + 1];
            return (
              <div key={order.id} style={S.orderCard}>
                <div style={S.orderHeader}>
                  <div>
                    <p style={{ color: GOLD, fontFamily: 'monospace', fontSize: '0.8rem', margin: '0 0 3px', fontWeight: '700' }}>
                      {order.orderId}
                    </p>
                    <p style={{ color: '#555', fontSize: '0.7rem', margin: 0 }}>
                      {order.createdAt?.toDate?.().toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) || ''}
                    </p>
                  </div>
                  <div style={{ ...S.orderStatusBadge, background: sc.bg, color: sc.color, borderColor: sc.color + '44' }}>
                    {order.orderStatus?.toUpperCase()}
                  </div>
                </div>

                <div style={S.orderBody}>
                  <div style={{ flex: 1 }}>
                    <p style={{ color: 'white', fontWeight: '700', margin: '0 0 4px' }}>{order.productName}</p>
                    <p style={{ color: '#555', fontSize: '0.75rem', margin: '0 0 2px' }}>Qty: {order.qty}</p>
                    <p style={{ color: '#555', fontSize: '0.75rem', margin: '0 0 2px' }}>
                      Buyer: {order.buyerName} · {order.buyerPhone}
                    </p>
                    <p style={{ color: '#444', fontSize: '0.72rem', margin: 0 }}>📍 {order.deliveryAddress}</p>
                    {order.comment && (
                      <p style={{ color: '#555', fontSize: '0.72rem', margin: '6px 0 0', fontStyle: 'italic' }}>
                        💬 "{order.comment}"
                      </p>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ color: GOLD, fontWeight: '900', fontSize: '1.1rem', margin: '0 0 2px' }}>
                      ₦{Number(order.basePrice * order.qty).toLocaleString()}
                    </p>
                    <p style={{ color: '#444', fontSize: '0.65rem', margin: 0 }}>your earnings</p>
                  </div>
                </div>

                {/* Tracking bar */}
                <div style={S.trackBar}>
                  {ORDER_STATUSES.map((status, i) => {
                    const current = ORDER_STATUSES.indexOf(order.orderStatus);
                    const done = i <= current;
                    return (
                      <div key={status} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                        <div style={{
                          width: '18px', height: '18px', borderRadius: '50%',
                          background: done ? GOLD : '#1a2631',
                          border: `2px solid ${done ? GOLD : '#2a3641'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.55rem', color: done ? 'black' : '#333', fontWeight: '900',
                        }}>{done ? '✓' : ''}</div>
                        <span style={{ color: done ? GOLD : '#333', fontSize: '0.55rem', marginTop: '3px' }}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Advance status (merchant can move to dispatched/delivered) */}
                {nextStatus && ['processing', 'dispatched'].includes(order.orderStatus) && (
                  <button onClick={() => handleUpdateStatus(order.id, nextStatus)}
                    style={{ ...S.outlineBtn, fontSize: '0.75rem', padding: '8px 16px', width: 'auto', alignSelf: 'flex-start', marginTop: '10px' }}>
                    MARK AS {nextStatus.toUpperCase()} →
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// TAB: ANALYTICS
// ════════════════════════════════════════════════════════════
function AnalyticsTab({ orders, products }) {
  const [period,    setPeriod]    = useState('7D');
  const [chartType, setChartType] = useState('bar');

  const paidOrders = orders.filter(o => o.paymentStatus === 'paid');

  // Build chart data
  const chartData = buildChartData(paidOrders, period);
  const maxVal    = Math.max(...chartData.map(d => d.revenue), 1);

  // Top products
  const productRevMap = {};
  paidOrders.forEach(o => {
    const key = o.productId;
    if (!productRevMap[key]) productRevMap[key] = { name: o.productName || 'Unknown', revenue: 0, units: 0 };
    productRevMap[key].revenue += (o.basePrice || 0) * (o.qty || 0);
    productRevMap[key].units   += o.qty || 0;
  });
  const topProducts = Object.values(productRevMap).sort((a, b) => b.revenue - a.revenue).slice(0, 3);

  const totalRevenue = paidOrders.reduce((s, o) => s + (o.basePrice || 0) * (o.qty || 0), 0);
  const totalUnits   = paidOrders.reduce((s, o) => s + (o.qty || 0), 0);

  return (
    <div>
      <TabHeader title="ANALYTICS" sub="Sales performance & insights" />

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '24px' }}>
        {[
          { label: 'TOTAL REVENUE',   value: `₦${totalRevenue.toLocaleString()}`, color: GOLD },
          { label: 'UNITS SOLD',      value: totalUnits,                           color: '#4caf50' },
          { label: 'COMPLETED ORDERS', value: paidOrders.length,                  color: '#60a5fa' },
        ].map(c => (
          <div key={c.label} style={S.analyticsCard}>
            <p style={{ color: c.color, fontWeight: '900', fontSize: '1.5rem', margin: '0 0 4px' }}>{c.value}</p>
            <p style={{ color: '#444', fontSize: '0.62rem', letterSpacing: '1.5px', margin: 0 }}>{c.label}</p>
          </div>
        ))}
      </div>

      {/* Chart Controls */}
      <div style={S.chartControls}>
        <div style={{ display: 'flex', gap: '6px' }}>
          {['7D', '4W', '12M', 'ALL'].map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              style={{ ...S.chartPill, ...(period === p ? S.chartPillActive : {}) }}>{p}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          {[['bar', '▐'], ['line', '〜']].map(([t, icon]) => (
            <button key={t} onClick={() => setChartType(t)}
              style={{ ...S.chartPill, ...(chartType === t ? S.chartPillActive : {}) }}>{icon} {t}</button>
          ))}
        </div>
      </div>

      {/* SVG Chart */}
      <div style={S.chartBox}>
        {paidOrders.length === 0 ? (
          <EmptyState icon="📊" title="No sales data yet" sub="Your sales chart will appear once you receive orders." />
        ) : (
          <SalesChart data={chartData} maxVal={maxVal} type={chartType} />
        )}
      </div>

      {/* Top Products */}
      {topProducts.length > 0 && (
        <div style={{ marginTop: '28px' }}>
          <p style={S.sectionMiniTitle}>TOP PRODUCTS</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {topProducts.map((p, i) => (
              <div key={p.name} style={S.topProdRow}>
                <span style={{ color: GOLD, fontWeight: '900', fontSize: '1.1rem', minWidth: '24px' }}>#{i + 1}</span>
                <div style={{ flex: 1 }}>
                  <p style={{ color: 'white', fontWeight: '600', fontSize: '0.88rem', margin: '0 0 2px' }}>{p.name}</p>
                  <p style={{ color: '#555', fontSize: '0.72rem', margin: 0 }}>{p.units} units sold</p>
                </div>
                <span style={{ color: GOLD, fontWeight: '900' }}>₦{p.revenue.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Chart helpers ─────────────────────────────────────────────────────────────
function buildChartData(orders, period) {
  const now  = new Date();
  let points = [];

  if (period === '7D') {
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now); d.setDate(d.getDate() - i);
      const label = d.toLocaleDateString('en-NG', { weekday: 'short' });
      const revenue = orders.filter(o => {
        const od = o.createdAt?.toDate?.(); if (!od) return false;
        return od.toDateString() === d.toDateString();
      }).reduce((s, o) => s + (o.basePrice || 0) * (o.qty || 0), 0);
      points.push({ label, revenue });
    }
  } else if (period === '4W') {
    for (let i = 3; i >= 0; i--) {
      const start = new Date(now); start.setDate(start.getDate() - (i + 1) * 7);
      const end   = new Date(now); end.setDate(end.getDate() - i * 7);
      const label = `W${4 - i}`;
      const revenue = orders.filter(o => {
        const od = o.createdAt?.toDate?.(); if (!od) return false;
        return od >= start && od < end;
      }).reduce((s, o) => s + (o.basePrice || 0) * (o.qty || 0), 0);
      points.push({ label, revenue });
    }
  } else if (period === '12M') {
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const label = d.toLocaleDateString('en-NG', { month: 'short' });
      const revenue = orders.filter(o => {
        const od = o.createdAt?.toDate?.(); if (!od) return false;
        return od.getFullYear() === d.getFullYear() && od.getMonth() === d.getMonth();
      }).reduce((s, o) => s + (o.basePrice || 0) * (o.qty || 0), 0);
      points.push({ label, revenue });
    }
  } else {
    // ALL: group by month
    const monthMap = {};
    orders.forEach(o => {
      const od = o.createdAt?.toDate?.(); if (!od) return;
      const key = `${od.getFullYear()}-${String(od.getMonth() + 1).padStart(2, '0')}`;
      if (!monthMap[key]) monthMap[key] = { label: od.toLocaleDateString('en-NG', { month: 'short', year: '2-digit' }), revenue: 0 };
      monthMap[key].revenue += (o.basePrice || 0) * (o.qty || 0);
    });
    points = Object.entries(monthMap).sort(([a], [b]) => a.localeCompare(b)).map(([, v]) => v);
  }

  return points.length > 0 ? points : [{ label: 'No data', revenue: 0 }];
}

function SalesChart({ data, maxVal, type }) {
  const W = 580, H = 180, pad = { t: 16, r: 10, b: 36, l: 50 };
  const gW = W - pad.l - pad.r;
  const gH = H - pad.t - pad.b;
  const n  = data.length;
  const bW = Math.max(8, (gW / n) - 8);

  const pts = data.map((d, i) => ({
    x: pad.l + (i + 0.5) * (gW / n),
    y: pad.t + gH - (d.revenue / maxVal) * gH,
    revenue: d.revenue,
    label: d.label,
  }));

  const pathD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }}>
      {/* Y grid */}
      {[0, 0.25, 0.5, 0.75, 1].map(f => (
        <g key={f}>
          <line x1={pad.l} y1={pad.t + gH * (1 - f)} x2={pad.l + gW} y2={pad.t + gH * (1 - f)}
            stroke="#1a2631" strokeWidth="1" />
          <text x={pad.l - 4} y={pad.t + gH * (1 - f) + 4}
            textAnchor="end" fontSize="7" fill="#444">
            {f > 0 ? `₦${((maxVal * f) / 1000).toFixed(0)}k` : '0'}
          </text>
        </g>
      ))}

      {type === 'bar' ? (
        pts.map((p, i) => (
          <g key={i}>
            <rect x={p.x - bW / 2} y={p.y} width={bW} height={pad.t + gH - p.y}
              fill={GOLD} opacity="0.85" rx="3" />
            {p.revenue > 0 && (
              <text x={p.x} y={p.y - 4} textAnchor="middle" fontSize="6.5" fill={GOLD} fontWeight="bold">
                ₦{(p.revenue / 1000).toFixed(0)}k
              </text>
            )}
          </g>
        ))
      ) : (
        <>
          <defs>
            <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={GOLD} stopOpacity="0.3" />
              <stop offset="100%" stopColor={GOLD} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={`${pathD} L${pts[pts.length - 1].x},${pad.t + gH} L${pts[0].x},${pad.t + gH} Z`}
            fill="url(#lineGrad)" />
          <path d={pathD} fill="none" stroke={GOLD} strokeWidth="2"
            filter="drop-shadow(0 0 4px #B8860B88)" strokeLinejoin="round" />
          {pts.map((p, i) => (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r="4" fill={GOLD} />
              {p.revenue > 0 && (
                <text x={p.x} y={p.y - 8} textAnchor="middle" fontSize="6.5" fill={GOLD} fontWeight="bold">
                  ₦{(p.revenue / 1000).toFixed(0)}k
                </text>
              )}
            </g>
          ))}
        </>
      )}

      {/* X labels */}
      {pts.map((p, i) => (
        <text key={i} x={p.x} y={H - 4} textAnchor="middle" fontSize="7" fill="#555">{p.label}</text>
      ))}
    </svg>
  );
}

// ════════════════════════════════════════════════════════════
// TAB: PROFILE
// ════════════════════════════════════════════════════════════
export function ProfileTab_FIXED({ merchant, user, setMerchant }) {
  const [editing, setEditing] = React.useState(false);
  const [form,    setForm]    = React.useState({ ...merchant });
  const [loading, setLoading] = React.useState(false);
  const [newLogo, setNewLogo] = React.useState(null);

  // FIX: use setDoc with merge: true — works whether or not the doc exists
  const handleSave = async () => {
    setLoading(true);
    try {
      let logoUrl = merchant?.logoUrl || '';
      if (newLogo) {
        const r = ref(storage, `merchant_profiles/${user.uid}/logo_${Date.now()}`);
        await uploadBytes(r, newLogo);
        logoUrl = await getDownloadURL(r);
      }

      // setDoc with merge: true creates if missing, updates if exists
      // This is safe for super admins AND normal merchants
      await setDoc(doc(db, 'merchants', user.uid), {
        contactName:        form.contactName       || '',
        businessName:       form.businessName      || '',
        phone:              form.phone             || '',
        address:            form.address           || '',
        city:               form.city              || '',
        categories:         form.categories        || merchant?.categories || [],
        gender:             form.gender            || '',
        cacNumber:          form.cacNumber         || '',
        bankAccountNumber:  form.bankAccountNumber || '',
        bankName:           form.bankName          || '',
        bankCode:           form.bankCode          || '',
        logoUrl,
        profileImageUrl:    logoUrl,
        updatedAt:          serverTimestamp(),
        // Preserve critical status fields — do NOT overwrite them with undefined
        status:             merchant?.status || 'approved',
        uid:                user.uid,
        email:              user.email || '',
      }, { merge: true });

      setMerchant(p => ({ ...p, ...form, logoUrl }));
      setEditing(false);
      setNewLogo(null);
      toast.success('Profile updated!');
    } catch (err) {
      console.error('ProfileTab save error:', err);
      toast.error(`Save failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const shareProfile = () => {
    const url = `${window.location.origin}/merchant/${user.uid}`;
    navigator.clipboard.writeText(url);
    toast.success('Profile link copied!');
  };

  const fields = [
    ['contactName',       'Contact Name',   'text'],
    ['businessName',      'Business Name',  'text'],
    ['phone',             'Phone Number',   'tel'],
    ['address',           'Address',        'text'],
    ['city',              'City',           'text'],
    ['cacNumber',         'CAC Number',     'text'],
    ['bankAccountNumber', 'Account Number', 'text'],
    ['bankName',          'Bank Name',      'text'],
  ];

  const S_local = {
    sectionTitle: { color: GOLD, fontWeight: '900', letterSpacing: '2px', margin: '0 0 4px', fontSize: '1.2rem' },
    actionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '22px' },
    outlineBtn:   { background: 'transparent', color: GOLD, padding: '10px 20px', borderRadius: '50px', fontWeight: '700', border: `1.5px solid ${GOLD}55`, cursor: 'pointer', fontSize: '0.78rem', fontFamily: 'inherit' },
    goldBtn:      { background: GOLD, color: 'black', padding: '15px', borderRadius: '50px', fontWeight: '900', border: 'none', cursor: 'pointer', fontSize: '0.88rem', letterSpacing: '1px', fontFamily: 'inherit' },
    input:        { background: '#060c11', border: `1px solid ${BORDER}`, padding: '12px 14px', borderRadius: '10px', color: 'white', fontSize: '0.88rem', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' },
  };

  return (
    <div>
      <div style={S_local.actionHeader}>
        <div>
          <h2 style={S_local.sectionTitle}>MERCHANT PROFILE</h2>
          {merchant?.isSuperAdminView && (
            <p style={{ color: '#f59e0b', fontSize: '0.72rem', margin: '4px 0 0', fontWeight: '600' }}>
              ⚡ Super Admin — profile will be created if it doesn't exist
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={shareProfile} style={S_local.outlineBtn}>🔗 SHARE PROFILE</button>
          <button
            onClick={() => editing ? handleSave() : setEditing(true)}
            disabled={loading}
            style={{ ...S_local.goldBtn, width: 'auto', padding: '10px 20px', fontSize: '0.78rem', opacity: loading ? 0.7 : 1 }}>
            {loading ? 'SAVING...' : editing ? '💾 SAVE' : '✏️ EDIT'}
          </button>
        </div>
      </div>

       {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: CARD_BG2, border: `2px solid ${BORDER}`, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {newLogo
            ? <img src={URL.createObjectURL(newLogo)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : merchant?.logoUrl
              ? <img src={merchant.logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <span style={{ color: GOLD, fontWeight: '900', fontSize: '2rem' }}>{(merchant?.businessName || 'M')[0]}</span>
          }
        </div>
        {editing && (
          <label style={{ cursor: 'pointer', color: GOLD, fontSize: '0.78rem', fontWeight: '700', border: `1px solid ${GOLD}44`, padding: '8px 16px', borderRadius: '50px', fontFamily: 'Montserrat, sans-serif' }}>
            📷 Change Logo
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => setNewLogo(e.target.files[0])} />
          </label>
        )}
      </div>

      {/* Fields grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
        {fields.map(([key, label, type]) => (
          <div key={key}>
            <p style={{ color: GOLD, fontSize: '0.6rem', letterSpacing: '2px', fontWeight: '900', margin: '0 0 5px' }}>{label}</p>
            {editing ? (
              <input
                type={type}
                value={form[key] || ''}
                onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                style={S_local.input}
              />
            ) : (
              <p style={{ color: '#ccc', fontSize: '0.88rem', margin: '4px 0 0', padding: '10px 0', borderBottom: `1px solid ${BORDER}` }}>
                {merchant?.[key] || <span style={{ color: '#333' }}>Not set</span>}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Read-only info */}
      <div style={{ marginTop: '20px' }}>
        {[
          ['Status',       merchant?.status?.toUpperCase(),                                          merchant?.status === 'approved' ? '#4caf50' : GOLD],
          ['Merchant Type',merchant?.merchantType ? merchant.merchantType.charAt(0).toUpperCase() + merchant.merchantType.slice(1) : '—', '#aaa'],
          ['Categories',   merchant?.categories?.length ? merchant.categories.join(', ').replace(/_/g, ' ').toUpperCase() : '—', '#aaa'],
          ['Terms Version',merchant?.termsVersion || '—',                                           '#444'],
          ['Member Since', merchant?.createdAt?.toDate?.()?.toLocaleDateString('en-NG', { day: 'numeric', month: 'long', year: 'numeric' }) || '—', '#444'],
        ].map(([label, val, color]) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${BORDER}` }}>
            <span style={{ color: '#555', fontSize: '0.78rem' }}>{label}</span>
            <span style={{ color: color || '#aaa', fontSize: '0.82rem', fontWeight: '600' }}>{val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SHARED SUB-COMPONENTS ────────────────────────────────────────────────────
function TabHeader({ title, sub, action }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '22px' }}>
      <div>
        <h2 style={{ color: GOLD, fontWeight: '900', letterSpacing: '2px', margin: '0 0 4px', fontSize: '1.2rem' }}>{title}</h2>
        {sub && <p style={{ color: '#444', fontSize: '0.75rem', margin: 0 }}>{sub}</p>}
      </div>
      {action}
    </div>
  );
}
function EmptyState({ icon, title, sub }) {
  return (
    <div style={{ textAlign: 'center', padding: '50px 20px', border: `1px dashed ${BORDER}`, borderRadius: '16px', background: `rgba(184,134,11,0.02)` }}>
      <div style={{ fontSize: '2.5rem', marginBottom: '10px' }}>{icon}</div>
      <p style={{ color: GOLD, fontWeight: '900', letterSpacing: '1.5px', margin: '0 0 6px', fontSize: '0.88rem' }}>{title}</p>
      <p style={{ color: '#444', fontSize: '0.8rem', maxWidth: '280px', margin: '0 auto', lineHeight: '1.6' }}>{sub}</p>
    </div>
  );
}
function FieldLabel({ children }) {
  return <p style={{ color: GOLD, fontSize: '0.6rem', letterSpacing: '2px', fontWeight: '900', margin: '0 0 5px' }}>{children}</p>;
}
function Req() { return <span style={{ color: '#ff4d4d' }}> *</span>; }
function LoadingScreen() {
  return (
    <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={S.spinner} />
        <p style={{ color: GOLD, letterSpacing: '3px', marginTop: '16px', fontSize: '0.75rem' }}>LOADING MERCHANT PORTAL...</p>
      </div>
      <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── STYLES ──────────────────────────────────────────────────────────────────
const S = {
  page:     { minHeight: '100vh', background: DEEP_BLACK, color: 'white', display: 'flex', fontFamily: "'Montserrat','Inter',sans-serif" },
  sidebar:  { width: '230px', minWidth: '230px', background: '#060c11', borderRight: `1px solid ${BORDER}`, display: 'flex', flexDirection: 'column', padding: '24px 0', minHeight: '100vh', position: 'sticky', top: 0 },
  sideTop:  { textAlign: 'center', padding: '0 16px 20px', borderBottom: `1px solid ${BORDER}` },
  sideTitle:{ color: GOLD, fontWeight: '900', letterSpacing: '3px', margin: '8px 0 0', fontSize: '0.65rem' },
  sideTitle2:{ color: '#444', fontWeight: '700', letterSpacing: '3px', margin: '0', fontSize: '0.6rem' },
  merchantCard: { padding: '20px 16px', textAlign: 'center', borderBottom: `1px solid ${BORDER}` },
  avatarCircle: { width: '60px', height: '60px', borderRadius: '50%', background: `${GOLD}15`, border: `2px solid ${GOLD}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px', overflow: 'hidden' },
  mBizName: { color: 'white', fontWeight: '700', fontSize: '0.88rem', margin: '0 0 3px' },
  mEmail:   { color: '#444', fontSize: '0.68rem', margin: '0 0 8px', wordBreak: 'break-all' },
  approvedBadge: { background: '#0a1f0a', color: '#4caf50', fontSize: '0.6rem', fontWeight: '900', padding: '3px 10px', borderRadius: '20px', border: '1px solid #4caf5044', display: 'inline-block' },
  nav: { flex: 1, padding: '12px 8px', display: 'flex', flexDirection: 'column', gap: '2px' },
  navBtn: { display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 12px', borderRadius: '10px', border: 'none', background: 'transparent', color: '#555', cursor: 'pointer', fontSize: '0.68rem', fontWeight: '700', letterSpacing: '1px', width: '100%', textAlign: 'left', fontFamily: 'inherit', position: 'relative' },
  navBtnActive: { background: `${GOLD}15`, color: GOLD, border: `1px solid ${GOLD}22` },
  badge:    { background: '#ff4d4d', color: 'white', borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: '900', marginLeft: 'auto' },
  signOutBtn: { margin: '12px 8px 0', background: 'transparent', border: `1px solid ${BORDER}`, color: '#555', padding: '10px', borderRadius: '10px', cursor: 'pointer', fontSize: '0.7rem', fontFamily: 'inherit', letterSpacing: '1px' },
  main:     { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  statsBar: { display: 'flex', gap: '1px', background: BORDER, borderBottom: `1px solid ${BORDER}` },
  statCard: { flex: 1, background: CARD_BG, padding: '16px 12px', textAlign: 'center' },
  content:  { flex: 1, padding: '28px 32px', overflowY: 'auto' },
  filterRow:{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' },
  filterPill: { background: CARD_BG, border: `1px solid ${BORDER}`, color: '#555', padding: '6px 14px', borderRadius: '20px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: '600', fontFamily: 'inherit' },
  filterPillActive: { background: `${GOLD}15`, border: `1px solid ${GOLD}44`, color: GOLD },
  productGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '14px' },
  productCard: { background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: '14px', overflow: 'hidden' },
  prodImgBox: { position: 'relative', aspectRatio: '1/1', background: CARD_BG2 },
  prodImg:    { width: '100%', height: '100%', objectFit: 'cover' },
  statusBadge: { position: 'absolute', top: '8px', left: '8px', fontSize: '0.58rem', fontWeight: '900', padding: '3px 8px', borderRadius: '20px', letterSpacing: '0.5px' },
  actionBtn: { flex: 1, background: CARD_BG2, border: `1px solid ${BORDER}`, color: '#aaa', padding: '7px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.72rem', fontFamily: 'inherit' },
  orderCard: { background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: '14px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' },
  orderHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  orderStatusBadge: { fontSize: '0.6rem', fontWeight: '900', padding: '4px 10px', borderRadius: '20px', border: '1px solid', letterSpacing: '0.5px' },
  orderBody: { display: 'flex', gap: '16px', justifyContent: 'space-between' },
  trackBar: { display: 'flex', justifyContent: 'space-between', background: CARD_BG2, borderRadius: '10px', padding: '10px' },
  analyticsCard: { background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: '14px', padding: '16px', textAlign: 'center' },
  chartControls: { display: 'flex', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' },
  chartBox: { background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: '14px', padding: '20px' },
  chartPill: { background: CARD_BG, border: `1px solid ${BORDER}`, color: '#555', padding: '5px 12px', borderRadius: '20px', cursor: 'pointer', fontSize: '0.7rem', fontFamily: 'inherit' },
  chartPillActive: { background: `${GOLD}15`, border: `1px solid ${GOLD}44`, color: GOLD, fontWeight: '700' },
  topProdRow: { display: 'flex', alignItems: 'center', gap: '12px', background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: '10px', padding: '12px 14px' },
  sectionMiniTitle: { color: GOLD, fontWeight: '900', fontSize: '0.62rem', letterSpacing: '2.5px', margin: '0 0 12px' },
  walletCard: { background: CARD_BG, borderRadius: '14px', padding: '18px', textAlign: 'center' },
  withdrawBox: { background: CARD_BG, border: `1px solid ${GOLD}33`, borderRadius: '14px', padding: '20px' },
  txRow: { display: 'flex', gap: '12px', justifyContent: 'space-between', background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: '10px', padding: '12px 14px' },
  feeBox: { background: `rgba(184,134,11,0.06)`, border: `1px solid ${GOLD}33`, borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' },
  feeRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  feeLabel: { color: '#888', fontSize: '0.78rem' },
  toggleBtn: { flex: 1, padding: '11px', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '800', fontSize: '0.72rem', letterSpacing: '0.5px', fontFamily: 'inherit' },
  toggleActive: { background: GOLD, color: 'black' },
  toggleInactive: { background: CARD_BG2, color: '#555', border: `1px solid ${BORDER}` },
  goldBtn: { background: GOLD, color: 'black', padding: '15px', borderRadius: '50px', fontWeight: '900', border: 'none', cursor: 'pointer', fontSize: '0.88rem', letterSpacing: '1px', width: '100%', fontFamily: 'inherit' },
  outlineBtn: { background: 'transparent', color: GOLD, padding: '10px 20px', borderRadius: '50px', fontWeight: '700', border: `1.5px solid ${GOLD}55`, cursor: 'pointer', fontSize: '0.78rem', fontFamily: 'inherit' },
  input: { background: '#060c11', border: `1px solid ${BORDER}`, padding: '12px 14px', borderRadius: '10px', color: 'white', fontSize: '0.88rem', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' },
  spinner: { width: '40px', height: '40px', border: `3px solid ${GOLD}22`, borderTop: `3px solid ${GOLD}`, borderRadius: '50%', animation: 'spin 1s linear infinite' },
};
